import React, { useCallback, useEffect, useState } from 'react';
import CardShell from '@/components/CardShell';
import ErrorBanner from '@/components/ErrorBanner';
import OutlineButton from '@/components/OutlineButton';
import SchoolHeader from '@/components/SchoolHeader';
import Spinner from '@/components/Spinner';
import DeadLinkScreen from '@/screens/DeadLinkScreen';
import PaymentNotCompletedScreen from '@/screens/PaymentNotCompletedScreen';
import PaymentSuccessScreen from '@/screens/PaymentSuccessScreen';
import { getCheckoutResult } from '@/services/api';
import { parseHostedError } from '@/services/errors';

/**
 * Where Stripe Checkout sends a parent back after paying an invoice from
 * the app: `/pay/success` and `/pay/cancel`, both carrying
 * `?session_id=cs_…&invoice=<id>`. Which route we are on decides nothing
 * about the outcome — the API reads the session back from Stripe and says
 * whether it is paid, still processing (bank debit) or not completed; the
 * route only shapes the not-completed copy.
 *
 * The params stay in the URL on purpose (no replaceState): reload, back
 * and reopen must all be able to ask the same question again, and the
 * question is read-only.
 */
const readParams = () => {
	const params = new URLSearchParams(window.location.search);
	return { sessionId: params.get('session_id') || '', invoiceId: params.get('invoice') || '' };
};

const CheckoutResultFlow = ({ outcome }) => {
	// loading | paid | processing | verification_pending | not_completed | invalid | error
	const [step, setStep] = useState('loading');
	const [result, setResult] = useState(null);
	const [error, setError] = useState(null);

	const load = useCallback(async () => {
		const { sessionId, invoiceId } = readParams();
		if (!sessionId || !invoiceId) {
			setStep('invalid');
			return;
		}

		setStep('loading');
		setError(null);
		try {
			const data = await getCheckoutResult(sessionId, invoiceId);
			setResult(data);
			setStep(
				['paid', 'processing', 'verification_pending', 'not_completed'].includes(data?.state) ? data.state : 'invalid'
			);
		} catch (err) {
			const parsed = parseHostedError(err);
			if (parsed.type === 'dead_link') {
				setStep('invalid');
				return;
			}
			setError(parsed.message);
			setStep('error');
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const body = () => {
		if (step === 'loading') return <Spinner label="Checking your payment…" />;
		if (step === 'invalid') return <DeadLinkScreen variant="link_not_found" />;
		if (step === 'error') {
			return (
				<div className="flex flex-col gap-[18px]">
					<ErrorBanner>{error}</ErrorBanner>
					<OutlineButton onClick={load}>Try again</OutlineButton>
				</div>
			);
		}
		if (step === 'not_completed') return <PaymentNotCompletedScreen result={result} outcome={outcome} />;

		return <PaymentSuccessScreen result={result} />;
	};

	const school = result?.school;

	return (
		<CardShell>
			{school && step !== 'loading' && step !== 'invalid' ? (
				<SchoolHeader name={school.name} logo={school.logo} />
			) : null}
			{body()}
		</CardShell>
	);
};

export default CheckoutResultFlow;
